/**
 * MOTOR ÚNICO DE COSTOS DEL ER v2 (fase 2).
 *
 * Agrega las facturas clasificadas por `cuenta_er` en una serie mensual por cuenta.
 * TODAS las vistas del v2 (fila, Contribución, Costos Variables, Costos Fijos, EBITDA
 * y sus YTD) leen de acá — no hay agregados paralelos que sincronizar a mano, que es
 * justamente la clase de bug que arrastra el v1.
 *
 * Fuentes: `facturas_sii_recibidas` + `facturas_externas` + `boletas_honorarios`.
 * NO `movimientos_caja`: eso es caja, no ER (regla 7.4 — mundos paralelos).
 *
 * Reglas de agregación:
 *  - SIN IVA. Afectas → `monto_neto`. Exentas/honorarios (neto 0) → `monto_exento`.
 *  - CAPEX afuera: se excluye `tipo_contable = 'activo'` (ya vive en Depreciación).
 *  - DIN (DTE 914, Aduana) afuera: no es gasto del período (`esDeclaracionIngreso`).
 *  - Notas de crédito (DTE 61) RESTAN. Vienen guardadas en positivo, así que se les
 *    invierte el signo; si no, inflarían el costo en vez de reducirlo.
 *  - Mes = mes de la fecha de emisión. Mes sin factura = 0. Sin proyección.
 *  - Externas: `monto_neto` en su moneda, convertido con el util compartido usando
 *    la fecha de emisión de la factura como TC.
 *  - Boletas de honorarios: SOLO las que computan (`computa` = status 'vigente'; las
 *    nula/vca están persistidas para trazabilidad y no devengan). Importe = `monto_bruto`:
 *    la BHE es exenta de segunda categoría, no lleva IVA, así que el bruto YA es el costo.
 *    La RETENCIÓN no entra acá — es un pasivo tributario que vive en caja (bloque F29),
 *    no un costo (regla 7.4). Sin notas de crédito: una BHE no las tiene, se anula.
 *
 * Las tres fuentes mapean por `cuenta_er` a la MISMA línea del ER: un honorario clasificado
 * en `contador` suma en Contador junto a las facturas de esa cuenta. No hay línea propia de
 * honorarios ni tratamiento especial por origen.
 */

import { esDeclaracionIngreso, esNotaCredito, signoNC } from "../../utils/dte";
import { parseLocalYMD } from "../../utils/fechasFuturosPanel";
import { getNombreProveedor } from "../../utils/proveedores";
import { crearRegistroFinancieroER } from "./registroFinancieroER";

// H7 — idéntico al de useRRHHCalculations.js (mismo wrapper compartido, 10.243).
const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);

/**
 * Corte duro del costo del SII. La contabilidad de la plataforma arranca el
 * 2026-01-01 (docs/modulos/er-v2.md — ex ARQUITECTURA §4.34): antes de esa fecha NO hay dato de costo, y no se
 * inventa. Toda factura con `fecha_emision` anterior se descarta, tanto del agregado
 * por cuenta como del total sin clasificar.
 *
 * Consecuencia esperada: en cualquier mes pre-2026 (el año 2025, o la porción 2025 de
 * la ventana LTM) las líneas de costo del SII quedan vacías. Las líneas OP (ingresos,
 * comisiones, RRHH) sí tienen histórico y no las afecta este corte.
 */
export const INICIO_COSTOS_SII = "2026-01-01";
const ANO_INICIO_COSTOS_SII = Number(INICIO_COSTOS_SII.slice(0, 4));

const meses12 = () => Array(12).fill(0);

/** Importe sin IVA de una factura SII: neto si es afecta, exento si es exenta/honorario. */
const importeSinIVA = (f) => {
  const neto = Number(f.monto_neto) || 0;
  if (neto > 0) return neto;
  return Number(f.monto_exento) || 0;
};

const mesDe = (fecha) => {
  if (!fecha) return null;
  // `fecha_emision` es DATE ("2026-03-17"): se parsea a mano para no depender del huso.
  const [ano, mes] = String(fecha).split("T")[0].split("-").map(Number);
  if (!ano || !mes || mes < 1 || mes > 12) return null;
  // Corte duro: nada anterior al inicio de la contabilidad entra al motor.
  if (ano < ANO_INICIO_COSTOS_SII) return null;
  return { ano, indice: mes - 1 };
};

/**
 * @param {object} params
 * @param {Array} params.facturasSII - Filas de facturas_sii_recibidas
 * @param {Array} params.facturasExternas - Filas de facturas_externas
 * @param {Array} params.boletasHonorarios - Filas de boletas_honorarios
 * @param {number} params.ano - Año a agregar
 * @param {string} params.pais - País (para la conversión de moneda)
 * @param {Function} params.convertirAMonedaLocal - Util compartido con el v1
 * @returns {{ porCuenta: Function, sinClasificarMensual: number[], cuentasConDatos: string[] }}
 */
export const agregarCostosER = ({
  facturasSII,
  facturasExternas,
  boletasHonorarios,
  ano,
  pais,
  convertirAMonedaLocal,
  // Mapa { [id]: { nombre, alias } } para MOSTRAR el alias del proveedor (regla 10.1) en el
  // detalle, en vez de la razón social cruda snapshoteada en el documento. Default {} para no
  // romper si algún caller no lo pasa (cae al snapshot). El matching sigue por id (regla 10.7);
  // los importes/cuentas/totales NO cambian: esto es solo el string que se muestra.
  proveedoresPorId = {},
}) => {
  const acumulado = {};
  const sinClasificarMensual = meses12();

  // Detalle por (cuenta, mes): las MISMAS facturas que se suman, con el MISMO importe.
  // Es la fuente del modal de detalle, para que su total sea igual al valor de la celda
  // por construccion y no por una query paralela.
  const detalle = {};

  // PASO 3.5 (Fase 3): cada factura que `sumar()` agrega también se transporta como
  // RegistroFinancieroER — mecánica interna de este archivo INTACTA (nada cambia en cómo se
  // calcula `monto`), solo se agrega esta segunda forma de salida para que `estadoResultadosER`
  // pueda agregar costos e ingresos con el mismo código (sección 2.4 del diseño). Bloque
  // "COSTO_SII": el bloque final (A/C variable, o FIJO) lo resuelve el mapeo cuenta→bloque de
  // `index.js`, aguas abajo — acá no se adivina. `fecha` = la fecha real de la factura (siempre
  // existe: `mesDe()` ya descartó las sin fecha_emision antes de llegar a `sumar()`).
  const registros = [];

  const sumar = (cuenta, indice, monto, factura) => {
    if (!monto) return;
    if (!acumulado[cuenta]) acumulado[cuenta] = meses12();
    acumulado[cuenta][indice] += monto;
    if (!detalle[cuenta]) detalle[cuenta] = Array.from({ length: 12 }, () => []);
    detalle[cuenta][indice].push({ ...factura, importe: monto });
    registros.push(
      crearRegistroFinancieroER({
        fecha: factura?.fecha || null,
        ano,
        mesIndice: indice,
        montoLocal: monto,
        bloque: "COSTO_SII",
        concepto: cuenta,
        pais,
        meta: factura,
      })
    );
  };

  // ── Fase 2: vínculos NC↔factura (SOLO lado costos / recibidas) ──────────────────────────────
  // Ids a EXCLUIR enteros del ER (ni suman ni restan, en ningún mes), resueltos ANTES de agregar:
  //   · NC 'anula_error' CON documento_referenciado_id: se excluyen la NC Y el documento anulado
  //     — la anulación cancela ambos, como si no existieran (regla 10.223).
  //   · NC 'anula_error' SIN documento_referenciado_id: anulación confirmada manualmente (CodRef=1
  //     del SII) que no se pudo vincular a una factura puntual — típico de proveedores recurrentes
  //     que emiten varias facturas del mismo monto (Entel telefonía, Fuenzalida contador). NO se
  //     excluye: entra al loop normal y resta en su cuenta_er (signo −1). Como la NC tiene el MISMO
  //     monto y la MISMA cuenta que el documento anulado, restarla equivale a que una factura
  //     recurrente igual nunca hubiera existido, sin necesidad de saber cuál (regla 10.225). El total
  //     anual de la cuenta queda exacto; la reducción cae en el mes de la NC (igual que un
  //     'ajuste_comercial'). Caja NO cambia: `ncAnulaError.js` descarta las 'anula_error' sin refId
  //     (siguen aplicándose como crédito FIFO en el ledger). Ver P2.68 en el ROADMAP.
  //   · NC SIN clasificar (tipo_relacion_nc null): se excluye la propia NC — ya no resta a ciegas en
  //     su mes (mismo trato que una factura sin clasificar: no entra).
  // La NC 'ajuste_comercial' NO se excluye: mantiene el comportamiento actual (resta con signo −1 en
  // su mes; su documento referenciado cuenta normal). Una factura NO referenciada por ninguna NC no
  // se toca. Solo aplica a `facturas_sii_recibidas` (los loops de externas/honorarios no cambian).
  const excluidosNC = new Set();
  (facturasSII || []).forEach((f) => {
    if (!esNotaCredito(f)) return;
    const refId = f.documento_referenciado_id;
    if (f.tipo_relacion_nc === "anula_error" && refId != null) {
      excluidosNC.add(Number(f.id));
      excluidosNC.add(Number(refId));
    } else if (f.tipo_relacion_nc === "anula_error" && refId == null) {
      // Anulación confirmada sin factura puntual identificable: NO se excluye → resta en su cuenta_er.
      // no-op deliberado (no se agrega a excluidosNC).
    } else if (f.tipo_relacion_nc === "ajuste_comercial" && refId != null) {
      // Sin cambios: la NC resta en su mes; el documento referenciado cuenta normal.
    } else {
      // Sin clasificar (o estado inconsistente): la NC no entra al ER.
      excluidosNC.add(Number(f.id));
    }
  });

  (facturasSII || []).forEach((f) => {
    // Fase 2: NC anuladoras + su documento anulado, y NC sin vincular, quedan FUERA del ER.
    if (excluidosNC.has(Number(f.id))) return;
    // CAPEX afuera: ya está contabilizado vía Depreciación / Amortización.
    if (f.tipo_contable === "activo") return;
    // DIN (Aduana, tipo_dte=914): no es gasto del período — defensa en profundidad, hoy ya sale
    // excluida por no tener cuenta_er clasificada (ver documentosCosto.js/CLASIFICACION.NO_APLICA).
    if (esDeclaracionIngreso(f)) return;
    const fecha = mesDe(f.fecha_emision);
    if (!fecha || fecha.ano !== ano) return;

    const signo = signoNC(f);
    const base = importeSinIVA(f);
    const monto = signo * base;

    const cuenta = f.cuenta_er;
    // `''` cuenta como sin clasificar igual que NULL (hay filas con string vacío).
    if (!cuenta) {
      sinClasificarMensual[fecha.indice] += monto;
      return;
    }

    const meta = {
      proveedor: getNombreProveedor(proveedoresPorId[f.proveedor_id]) || f.razon_social_emisor || "—",
      fecha: String(f.fecha_emision).split("T")[0],
      numero: f.folio != null ? String(f.folio) : "—",
      origen: "SII",
      // Metadato para la UI. No afecta el importe ni el total: la NC ya viene con
      // signo negativo desde `signo`.
      esNotaCredito: signo === -1,
    };

    // Carve-out: reparto MANUAL de UNA factura de gasto entre 2 cuentas del ER (caso Entel:
    // internet + telefonía en un solo neto). Se aplica SOLO si la factura tiene `cuenta_er_2`
    // y `monto_er_2` seteados, NO es nota de crédito (el reparto es por monto absoluto — una
    // NC lo invertiría mal, va entera por el signo) y el monto deja saldo POSITIVO en la cuenta
    // principal (`monto_er_2 < base`). Ambos tramos pasan por el MISMO `sumar()`, así que
    // `porCuenta` y `detalleDe` quedan consistentes y el invariante Σ detalle == celda se
    // sostiene solo. Es reasignación entre líneas: la suma de los dos tramos == base, el costo
    // total (y el EBITDA) no cambia.
    const montoEr2 = Number(f.monto_er_2) || 0;
    if (signo === 1 && f.cuenta_er_2 && montoEr2 > 0 && montoEr2 < base) {
      sumar(f.cuenta_er_2, fecha.indice, montoEr2, meta);
      sumar(cuenta, fecha.indice, base - montoEr2, meta);
      return;
    }

    sumar(cuenta, fecha.indice, monto, meta);
  });

  (facturasExternas || []).forEach((f) => {
    const fecha = mesDe(f.fecha_emision);
    if (!fecha || fecha.ano !== ano) return;
    if (!f.cuenta_er) return; // hoy no hay externas sin clasificar, pero no se asume

    // Multi-moneda: se convierte con el TC de la fecha de emisión de la factura.
    const monto = convertirAMonedaLocal(
      Number(f.monto_neto) || 0,
      f.moneda,
      pais,
      null,
      fecha.indice + 1,
      ano,
      String(f.fecha_emision).split("T")[0]
    );
    sumar(f.cuenta_er, fecha.indice, monto, {
      proveedor: getNombreProveedor(proveedoresPorId[f.proveedor_id]) || f.proveedor_nombre || "—",
      fecha: String(f.fecha_emision).split("T")[0],
      numero: f.numero_documento || "—",
      origen: "Externa",
      esNotaCredito: false,
    });
  });

  (boletasHonorarios || []).forEach((b) => {
    // Solo las vigentes devengan. `computa` es la columna generada (status = 'vigente'):
    // se lee el flag y no el status, para que un valor nuevo de Clay quede fuera por
    // default en vez de colarse (ver migración 20260719_boletas_honorarios_status_libre).
    if (!b.computa) return;
    // CAPEX afuera, igual que las SII. Hoy ninguna boleta se clasifica como activo — el
    // path honorario→activo no existe todavía —, pero el gate se pone desde el principio
    // para que el día que exista no haya que acordarse de agregarlo.
    if (b.tipo_contable === "activo") return;
    const fecha = mesDe(b.fecha_emision);
    if (!fecha || fecha.ano !== ano) return;

    // La BHE es EXENTA: el bruto ya es el costo sin IVA. La retención NO se resta — es
    // dinero del prestador que la empresa entera al SII en su nombre, no un menor gasto.
    const monto = Number(b.monto_bruto) || 0;

    const cuenta = b.cuenta_er;
    if (!cuenta) {
      sinClasificarMensual[fecha.indice] += monto;
      return;
    }
    sumar(cuenta, fecha.indice, monto, {
      proveedor: getNombreProveedor(proveedoresPorId[b.proveedor_id]) || b.emisor_nombre || "—",
      fecha: String(b.fecha_emision).split("T")[0],
      numero: b.folio != null ? String(b.folio) : "—",
      origen: "Honorario",
      esNotaCredito: false,
    });
  });

  const CERO = meses12();
  const SIN_DETALLE = [];
  return {
    /** Serie mensual (12) de una cuenta. Cuenta sin facturas → 12 ceros. */
    porCuenta: (cuenta) => acumulado[cuenta] || CERO,
    /**
     * Facturas que componen la celda (cuenta, mes). La suma de sus `importe` es, por
     * construccion, exactamente `porCuenta(cuenta)[mes]`.
     */
    detalleDe: (cuenta, mesIndex) => detalle[cuenta]?.[mesIndex] || SIN_DETALLE,
    sinClasificarMensual,
    cuentasConDatos: Object.keys(acumulado).sort(),
    /**
     * PASO 3.5: todas las facturas que SÍ entraron (misma fuente que `porCuenta`/`detalleDe`,
     * nunca las sin clasificar ni las excluidas por NC) como `RegistroFinancieroER[]`. Σ de
     * `registros` filtrados por (cuenta, mes) === `porCuenta(cuenta)[mes]`, por construcción
     * (mismo `sumar()`).
     */
    registros,
  };
};

/**
 * PASO 3.5: RRHH como fuente de `RegistroFinancieroER[]` — `fecha: null` SIEMPRE (decisión 2,
 * instrucción explícita de este paso: "RRHH como fuente fecha null"). La nómina no tiene
 * documento propio, así que no hay una fecha real que ofrecer todavía; la medición previa a
 * 3.8 (riesgo 3 del diseño, ajuste 4 de la revisión de Cowork) es la que decide si existe una
 * fecha de liquidación/pago utilizable en otra tabla — ESTE paso no la investiga ni la asume.
 *
 * NO reimplementa el cálculo del sueldo (prorrateo, gratificación): recibe `getSueldoBase`, la
 * MISMA función inyectada que ya usa `useRRHHCalculations.js` (`getSueldoBaseVigente`/
 * `getSueldoBaseVigenteParaAno`/`getSueldoBaseVigenteChileFijo`/`getSueldoBaseVigentePeru`,
 * según el caller) — un registro por empleado y mes con sueldo > 0, réplica de
 * `useRRHHCalculations.js:209-215` (`rrhhMensual`, antes de sumar beneficios de Perú/egresos,
 * que van en `registrosEgresosRRHH` abajo).
 *
 * @param {object} p
 * @param {Array} p.empleados
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getSueldoBase - `(empleado, monthIndex) => number`, YA bound a año/país.
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosRRHH = ({ empleados, pais, ano, getSueldoBase }) => {
  const registros = [];
  for (let mesIndice = 0; mesIndice < 12; mesIndice++) {
    (empleados || []).forEach((emp) => {
      const montoLocal = getSueldoBase(emp, mesIndice);
      if (!montoLocal) return;
      registros.push(
        crearRegistroFinancieroER({
          fecha: null,
          ano,
          mesIndice,
          montoLocal,
          bloque: "FIJO",
          concepto: "rrhh",
          pais,
          meta: { id: emp.id, origen: "empleados" },
        })
      );
    });
  }
  return registros;
};

/**
 * PASO 3.5: beneficios de PERÚ (gratificación junio/diciembre, CTS mayo/noviembre) — réplica de
 * la rama Perú de `useRRHHCalculations.js:172-195` (`agregarBeneficios`). Son pagos EXTRA del
 * MISMO empleado en esos 4 meses fijos (se SUMAN a lo que `registrosRRHH` ya generó para ese
 * mes, no lo reemplazan) — usa la MISMA `getSueldoBase` inyectada, llamada con el mes fijo de
 * cada beneficio en vez del mes que se está iterando. Chile no tiene esta función: su
 * gratificación ya está dentro de `calcSueldoCore`/`getSueldoBase` (sin doble conteo, mismo
 * comentario que el hook viejo). `limitMonths` del hook viejo (recorte por `monthsToShow`) NO
 * se aplica acá — es una decisión de VISTA, igual que en todo `ingresosER`/`costosER` (el
 * recorte por mes-en-curso no es del motor, ver `registrosVentas` en `ingresosER.js`).
 */
export const registrosBeneficiosPeru = ({ empleados, ano, getSueldoBase }) => {
  const registros = [];
  const BENEFICIOS = [
    { mesIndice: 5, concepto: "rrhh_gratificacion_junio" },
    { mesIndice: 11, concepto: "rrhh_gratificacion_diciembre" },
    { mesIndice: 4, concepto: "rrhh_cts_mayo" },
    { mesIndice: 10, concepto: "rrhh_cts_noviembre" },
  ];
  BENEFICIOS.forEach(({ mesIndice, concepto }) => {
    (empleados || []).forEach((emp) => {
      const montoLocal = getSueldoBase(emp, mesIndice);
      if (!montoLocal) return;
      registros.push(
        crearRegistroFinancieroER({
          fecha: null,
          ano,
          mesIndice,
          montoLocal,
          bloque: "FIJO",
          concepto,
          pais: "Perú",
          meta: { id: emp.id, origen: "empleados" },
        })
      );
    });
  });
  return registros;
};

/**
 * PASO 3.5: egresos de empleados (`emp.egreso_fecha`/`emp.egreso_monto`) — réplica de
 * `useRRHHCalculations.js:154-166` (`calcEgresos`). `fecha: null` igual que RRHH (decisión 2,
 * mismo motivo arriba) — `emp.egreso_fecha` SÍ es una fecha real, pero decidir si usarla para
 * el TC es parte de la misma medición pendiente antes de 3.8, no de este paso; acá solo se usa
 * para ubicar el registro en su mes (`ano`/`mesIndice`), igual que hoy.
 */
export const registrosEgresosRRHH = ({ empleados, pais, ano }) => {
  const registros = [];
  (empleados || []).forEach((emp) => {
    if (!emp.egreso_fecha || !emp.egreso_monto) return;
    const fecha = fechaLocalDeNegocio(emp.egreso_fecha);
    if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return;
    registros.push(
      crearRegistroFinancieroER({
        fecha: null,
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal: Number(emp.egreso_monto) || 0,
        bloque: "FIJO",
        concepto: "rrhh_egreso",
        pais,
        meta: { id: emp.id, origen: "empleados" },
      })
    );
  });
  return registros;
};
